/**
 * Chat Response Cards
 *
 * Rich, interactive cards rendered inline within the Command Center chat.
 * These replace walls of text with visual, actionable summaries.
 *
 * Card types:
 * - AssetCard: Shows an asset summary with photo, health, and quick actions
 * - LocationCard: Shows a location summary with health and asset count
 * - HealthSummaryCard: Shows organization health overview
 * - StatCard: Shows a single key metric
 */

import { useCallback } from "react";
import { Button, Progress, Tag } from "antd";
import {
	ArrowRightOutlined,
	EnvironmentOutlined,
	HeartOutlined,
	ToolOutlined,
	DollarOutlined,
	WarningOutlined,
	FormOutlined,
} from "@ant-design/icons";
import { MdHvac } from "react-icons/md";
import { useUIModeStore } from "@/stores/ui-mode-store";
import { useNavigate } from "react-router-dom";
import "./ChatCards.scss";

// ─── Types ───

export interface AssetCardData {
	type: "asset";
	id: number | string;
	assetTag: string;
	assetType?: string;
	location?: string;
	locationId?: number | string;
	healthScore?: number;
	condition?: string;
	photoUrl?: string;
	manufacturer?: string;
	model?: string;
}

export interface LocationCardData {
	type: "location";
	id: number | string;
	name: string;
	address?: string;
	healthScore?: number;
	assetCount?: number;
	photoUrl?: string;
}

export interface HealthSummaryCardData {
	type: "health-summary";
	overallScore: number;
	totalAssets: number;
	poorHealth: number;
	pastUsefulLife: number;
	totalBudget?: number;
}

export interface StatCardData {
	type: "stat";
	label: string;
	value: string | number;
	icon?: "health" | "assets" | "budget" | "warning" | "location";
	trend?: "up" | "down" | "neutral";
}

export interface WorkOrderCardData {
	type: "work-order";
	id: number;
	workOrderNumber: string;
	title: string;
	status: string;
	priority: number;
	workType: string;
	locationName?: string;
	assigneeName?: string;
	isSlaBreached?: boolean;
}

export interface WorkOrderStatsCardData {
	type: "work-order-stats";
	total: number;
	open: number;
	inProgress: number;
	overdue: number;
	totalCost: number;
}

export interface CollectionRecordCardData {
	type: "collection-record";
	id: number;
	templateName: string;
	category?: string;
	locationName?: string;
	areaName?: string;
	status: string;
	healthScore?: number;
}

export interface CollectionStatsCardData {
	type: "collection-stats";
	total: number;
	completed: number;
	reviewed: number;
	flagged: number;
	promotionSuggestions: number;
	byCategory: Record<string, number>;
}

export type ChatCardData =
	| AssetCardData
	| LocationCardData
	| HealthSummaryCardData
	| StatCardData
	| WorkOrderCardData
	| WorkOrderStatsCardData
	| CollectionRecordCardData
	| CollectionStatsCardData;

// ─── Helper: Health color ───

const getHealthColor = (score: number): string => {
	if (score >= 80) return "#52c41a";
	if (score >= 60) return "#faad14";
	if (score >= 40) return "#fa8c16";
	return "#ff4d4f";
};

const getHealthLabel = (score: number): string => {
	if (score >= 80) return "Good";
	if (score >= 60) return "Fair";
	if (score >= 40) return "Poor";
	return "Critical";
};

// ─── Asset Card ───

const AssetCard = ({ data }: { data: AssetCardData }) => {
	const navigate = useNavigate();
	const { setMode } = useUIModeStore();

	const handleViewDetails = useCallback(() => {
		setMode("deep-data");
		navigate("/assets", { state: { selectedAssetId: String(data.id) } });
	}, [data.id, navigate, setMode]);

	return (
		<div className="chat-card chat-card-asset">
			<div className="chat-card-header">
				<div className="chat-card-icon asset-icon">
					<MdHvac />
				</div>
				<div className="chat-card-title-block">
					<span className="chat-card-label">Asset</span>
					<span className="chat-card-title">{data.assetTag}</span>
				</div>
				{data.healthScore != null && (
					<div className="chat-card-health">
						<Progress
							type="circle"
							percent={data.healthScore}
							size={40}
							strokeColor={getHealthColor(data.healthScore)}
							format={(p) => `${p}`}
						/>
					</div>
				)}
			</div>

			<div className="chat-card-body">
				{data.assetType && (
					<div className="chat-card-field">
						<span className="field-label">Type</span>
						<span className="field-value">{data.assetType}</span>
					</div>
				)}
				{data.manufacturer && (
					<div className="chat-card-field">
						<span className="field-label">Manufacturer</span>
						<span className="field-value">
							{data.manufacturer}
							{data.model ? ` / ${data.model}` : ""}
						</span>
					</div>
				)}
				{data.location && (
					<div className="chat-card-field">
						<span className="field-label">Location</span>
						<span className="field-value">
							<EnvironmentOutlined /> {data.location}
						</span>
					</div>
				)}
				{data.condition && (
					<div className="chat-card-field">
						<span className="field-label">Condition</span>
						<Tag
							color={
								data.condition === "Good"
									? "green"
									: data.condition === "Fair"
										? "gold"
										: "red"
							}
						>
							{data.condition}
						</Tag>
					</div>
				)}
			</div>

			<div className="chat-card-footer">
				<Button
					type="primary"
					size="small"
					icon={<ArrowRightOutlined />}
					onClick={handleViewDetails}
					className="chat-card-action"
				>
					View Details
				</Button>
			</div>
		</div>
	);
};

// ─── Location Card ───

const LocationCard = ({ data }: { data: LocationCardData }) => {
	const navigate = useNavigate();
	const { setMode } = useUIModeStore();

	const handleViewDetails = useCallback(() => {
		setMode("deep-data");
		navigate(`/locations/${data.id}`);
	}, [data.id, navigate, setMode]);

	return (
		<div className="chat-card chat-card-location">
			<div className="chat-card-header">
				<div className="chat-card-icon location-icon">
					<EnvironmentOutlined />
				</div>
				<div className="chat-card-title-block">
					<span className="chat-card-label">Location</span>
					<span className="chat-card-title">{data.name}</span>
				</div>
				{data.healthScore != null && (
					<div className="chat-card-health">
						<Progress
							type="circle"
							percent={data.healthScore}
							size={40}
							strokeColor={getHealthColor(data.healthScore)}
							format={(p) => `${p}`}
						/>
					</div>
				)}
			</div>

			<div className="chat-card-body">
				{data.address && (
					<div className="chat-card-field">
						<span className="field-label">Address</span>
						<span className="field-value">{data.address}</span>
					</div>
				)}
				{data.assetCount != null && (
					<div className="chat-card-field">
						<span className="field-label">Assets</span>
						<span className="field-value">{data.assetCount} equipment</span>
					</div>
				)}
			</div>

			<div className="chat-card-footer">
				<Button
					type="primary"
					size="small"
					icon={<ArrowRightOutlined />}
					onClick={handleViewDetails}
					className="chat-card-action"
				>
					View Location
				</Button>
			</div>
		</div>
	);
};

// ─── Health Summary Card ───

const HealthSummaryCard = ({ data }: { data: HealthSummaryCardData }) => {
	return (
		<div className="chat-card chat-card-health-summary">
			<div className="chat-card-header">
				<div className="chat-card-icon health-icon">
					<HeartOutlined />
				</div>
				<div className="chat-card-title-block">
					<span className="chat-card-label">Health Overview</span>
					<span className="chat-card-title">
						{getHealthLabel(data.overallScore)} — {data.overallScore}%
					</span>
				</div>
				<div className="chat-card-health">
					<Progress
						type="circle"
						percent={data.overallScore}
						size={48}
						strokeColor={getHealthColor(data.overallScore)}
						format={(p) => `${p}%`}
					/>
				</div>
			</div>

			<div className="chat-card-stats-grid">
				<div className="stat-cell">
					<ToolOutlined className="stat-icon" />
					<span className="stat-value">{data.totalAssets.toLocaleString()}</span>
					<span className="stat-label">Total Assets</span>
				</div>
				<div className="stat-cell warning">
					<WarningOutlined className="stat-icon" />
					<span className="stat-value">{data.poorHealth.toLocaleString()}</span>
					<span className="stat-label">Poor Health</span>
				</div>
				<div className="stat-cell danger">
					<WarningOutlined className="stat-icon" />
					<span className="stat-value">{data.pastUsefulLife.toLocaleString()}</span>
					<span className="stat-label">Past Useful Life</span>
				</div>
				{data.totalBudget != null && (
					<div className="stat-cell">
						<DollarOutlined className="stat-icon" />
						<span className="stat-value">
							${(data.totalBudget / 1000000).toFixed(1)}M
						</span>
						<span className="stat-label">10-Year Budget</span>
					</div>
				)}
			</div>
		</div>
	);
};

// ─── Stat Card ───

const STAT_ICONS = {
	health: <HeartOutlined />,
	assets: <ToolOutlined />,
	budget: <DollarOutlined />,
	warning: <WarningOutlined />,
	location: <EnvironmentOutlined />,
};

const StatCard = ({ data }: { data: StatCardData }) => {
	return (
		<div className="chat-card chat-card-stat">
			{data.icon && (
				<div className="stat-card-icon">
					{STAT_ICONS[data.icon]}
				</div>
			)}
			<div className="stat-card-content">
				<span className="stat-card-value">{data.value}</span>
				<span className="stat-card-label">{data.label}</span>
			</div>
		</div>
	);
};

// ─── Work Order Card ───

const PRIORITY_COLORS: Record<number, string> = {
	1: "#ff4d4f",
	2: "#fa8c16",
	3: "#faad14",
	4: "#1890ff",
	5: "#8c8c8c",
};

const PRIORITY_NAMES: Record<number, string> = {
	1: "Emergency",
	2: "Urgent",
	3: "High",
	4: "Medium",
	5: "Low",
};

const WorkOrderChatCard = ({ data }: { data: WorkOrderCardData }) => {
	const navigate = useNavigate();
	const { setMode } = useUIModeStore();

	const handleView = useCallback(() => {
		setMode("deep-data");
		navigate(`/work-orders/${data.id}`);
	}, [data.id, navigate, setMode]);

	return (
		<div className="chat-card chat-card-wo" onClick={handleView} style={{ cursor: "pointer" }}>
			<div className="chat-card-header">
				<div className="chat-card-icon" style={{ background: PRIORITY_COLORS[data.priority] ?? "#8c8c8c" }}>
					<span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>P{data.priority}</span>
				</div>
				<div className="chat-card-title-group">
					<span className="chat-card-title">{data.title}</span>
					<span className="chat-card-subtitle" style={{ fontFamily: "monospace" }}>{data.workOrderNumber}</span>
				</div>
				{data.isSlaBreached && (
					<span style={{ color: "#ff4d4f", fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>SLA Breached</span>
				)}
			</div>
			<div className="chat-card-body" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
				<span className="chat-card-meta-item">
					<strong>Status:</strong> {data.status.replace(/_/g, " ")}
				</span>
				<span className="chat-card-meta-item">
					<strong>Type:</strong> {data.workType}
				</span>
				{data.locationName && (
					<span className="chat-card-meta-item">
						<strong>Location:</strong> {data.locationName}
					</span>
				)}
				{data.assigneeName && (
					<span className="chat-card-meta-item">
						<strong>Assigned:</strong> {data.assigneeName}
					</span>
				)}
			</div>
		</div>
	);
};

// ─── Work Order Stats Card ───

const WorkOrderStatsChatCard = ({ data }: { data: WorkOrderStatsCardData }) => {
	const navigate = useNavigate();
	const { setMode } = useUIModeStore();

	return (
		<div className="chat-card chat-card-wo-stats">
			<div className="chat-card-header">
				<div className="chat-card-icon" style={{ background: "#92278f" }}>
					<span style={{ fontSize: 14, color: "#fff" }}>WO</span>
				</div>
				<div className="chat-card-title-group">
					<span className="chat-card-title">Work Order Overview</span>
					<span className="chat-card-subtitle">{data.total} total work orders</span>
				</div>
			</div>
			<div className="chat-card-body" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: "#1890ff" }}>{data.open}</span>
					<span className="wo-stats-lbl">Open</span>
				</div>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: "#52c41a" }}>{data.inProgress}</span>
					<span className="wo-stats-lbl">In Progress</span>
				</div>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: data.overdue > 0 ? "#ff4d4f" : "#8c8c8c" }}>{data.overdue}</span>
					<span className="wo-stats-lbl">Overdue</span>
				</div>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: "#92278f" }}>${data.totalCost.toLocaleString()}</span>
					<span className="wo-stats-lbl">Total Cost</span>
				</div>
			</div>
			<button
				className="chat-card-action"
				onClick={() => {
					setMode("deep-data");
					navigate("/work-orders");
				}}
			>
				View All Work Orders →
			</button>
		</div>
	);
};

// ─── Collection Record Card ───

const CollectionRecordChatCard = ({ data }: { data: CollectionRecordCardData }) => {
	const navigate = useNavigate();
	const { setMode } = useUIModeStore();

	const handleView = useCallback(() => {
		setMode("deep-data");
		navigate(`/data-collection/${data.id}`);
	}, [data.id, navigate, setMode]);

	const statusColor =
		data.status === "COMPLETED" ? "success" :
		data.status === "REVIEWED" ? "blue" :
		data.status === "FLAGGED" ? "error" :
		data.status === "PROCESSING" ? "processing" : "default";

	return (
		<div className="chat-card chat-card-dc" onClick={handleView} style={{ cursor: "pointer" }}>
			<div className="chat-card-header">
				<div className="chat-card-icon" style={{ background: "#9c27b0" }}>
					<FormOutlined style={{ color: "#fff", fontSize: 14 }} />
				</div>
				<div className="chat-card-title-group">
					<span className="chat-card-title">{data.templateName}</span>
					{data.category && (
						<span className="chat-card-subtitle">{data.category}</span>
					)}
				</div>
				{data.healthScore != null && (
					<div className="chat-card-health">
						<Progress
							type="circle"
							percent={data.healthScore}
							size={36}
							strokeColor={getHealthColor(data.healthScore)}
							format={(p) => `${p}`}
						/>
					</div>
				)}
			</div>
			<div className="chat-card-body" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
				<Tag color={statusColor}>{data.status}</Tag>
				{data.locationName && (
					<span className="chat-card-meta-item">
						<EnvironmentOutlined /> {data.locationName}
					</span>
				)}
				{data.areaName && (
					<span className="chat-card-meta-item">{data.areaName}</span>
				)}
			</div>
		</div>
	);
};

// ─── Collection Stats Card ───

const CollectionStatsChatCard = ({ data }: { data: CollectionStatsCardData }) => {
	const navigate = useNavigate();
	const { setMode } = useUIModeStore();

	return (
		<div className="chat-card chat-card-dc-stats">
			<div className="chat-card-header">
				<div className="chat-card-icon" style={{ background: "#9c27b0" }}>
					<FormOutlined style={{ color: "#fff", fontSize: 14 }} />
				</div>
				<div className="chat-card-title-group">
					<span className="chat-card-title">Data Collection Overview</span>
					<span className="chat-card-subtitle">{data.total} total records</span>
				</div>
			</div>
			<div className="chat-card-body" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: "#52c41a" }}>{data.completed}</span>
					<span className="wo-stats-lbl">Completed</span>
				</div>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: "#1890ff" }}>{data.reviewed}</span>
					<span className="wo-stats-lbl">Reviewed</span>
				</div>
				<div className="wo-stats-mini">
					<span className="wo-stats-num" style={{ color: data.flagged > 0 ? "#ff4d4f" : "#8c8c8c" }}>{data.flagged}</span>
					<span className="wo-stats-lbl">Flagged</span>
				</div>
				{data.promotionSuggestions > 0 && (
					<div className="wo-stats-mini">
						<span className="wo-stats-num" style={{ color: "#9c27b0" }}>{data.promotionSuggestions}</span>
						<span className="wo-stats-lbl">Promotions</span>
					</div>
				)}
			</div>
			{Object.keys(data.byCategory).length > 0 && (
				<div className="chat-card-body" style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingTop: 0 }}>
					{Object.entries(data.byCategory).map(([cat, count]) => (
						<Tag key={cat} color="purple">{cat}: {count}</Tag>
					))}
				</div>
			)}
			<button
				className="chat-card-action"
				onClick={() => {
					setMode("deep-data");
					navigate("/data-collection");
				}}
			>
				View All Collections →
			</button>
		</div>
	);
};

// ─── Card Renderer ───

export const ChatCard = ({ data }: { data: ChatCardData }) => {
	switch (data.type) {
		case "asset":
			return <AssetCard data={data} />;
		case "location":
			return <LocationCard data={data} />;
		case "health-summary":
			return <HealthSummaryCard data={data} />;
		case "stat":
			return <StatCard data={data} />;
		case "work-order":
			return <WorkOrderChatCard data={data} />;
		case "work-order-stats":
			return <WorkOrderStatsChatCard data={data} />;
		case "collection-record":
			return <CollectionRecordChatCard data={data} />;
		case "collection-stats":
			return <CollectionStatsChatCard data={data} />;
		default:
			return null;
	}
};
